import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button, Modal } from "../components";
import { Toggle, SettingsSection } from "../components/settings";
import { useAuth } from "../hooks/useAuth";
import { usePageTitle } from "../hooks";
import { apiGet, apiPost } from "../lib/api";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

/**
 * Email preferences interface
 */
interface EmailPreferences {
  marketing: boolean;
  product_updates: boolean;
  tips_and_tricks: boolean;
  sequences: boolean;
  frequency: "normal" | "reduced" | "minimal";
  paused_until: string | null;
}

/**
 * EmailPreferencesPage - Email preference center for parents.
 *
 * This page works in two modes:
 * 1. Authenticated mode: User is logged in, uses standard auth
 * 2. Token mode: User accessed via unsubscribe link, uses token for auth
 *
 * Compliant with CAN-SPAM and GDPR requirements:
 * - Easy opt-out mechanism
 * - No login required to unsubscribe
 * - Granular preference controls
 */
export function EmailPreferencesPage() {
  usePageTitle("Email Preferences");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { user, isLoading: authLoading } = useAuth();

  // State
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Determine access mode
  const isTokenMode = !!token;
  const hasAccess = isTokenMode || !!user;

  // Fetch preferences
  const fetchPreferences = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isTokenMode) {
        // Token-based access (no auth required)
        const response = await fetch(
          `${API_URL}/api/email/preferences/by-token?token=${encodeURIComponent(token)}`,
          { method: "GET" }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || "Failed to load preferences");
        }

        setPreferences(data.preferences);
        setMaskedEmail(data.email);
      } else {
        // Authenticated access
        const response = await apiGet<{ preferences: EmailPreferences }>(
          "/api/email/preferences"
        );

        if (response.error) {
          throw new Error(response.error.message);
        }

        setPreferences(response.data?.preferences || null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load preferences");
    } finally {
      setIsLoading(false);
    }
  }, [token, isTokenMode]);

  // Load preferences on mount
  useEffect(() => {
    if (!authLoading && hasAccess) {
      fetchPreferences();
    } else if (!authLoading && !hasAccess) {
      setIsLoading(false);
    }
  }, [authLoading, hasAccess, fetchPreferences]);

  // Update a single preference
  const updatePreference = useCallback(
    <K extends keyof EmailPreferences>(key: K, value: EmailPreferences[K]) => {
      if (!preferences) return;

      setPreferences((prev) => (prev ? { ...prev, [key]: value } : prev));
      setHasChanges(true);
      setSaveSuccess(false);
    },
    [preferences]
  );

  // Save preferences
  const savePreferences = useCallback(async () => {
    if (!preferences) return;

    setIsSaving(true);
    setError(null);

    try {
      if (isTokenMode) {
        const response = await fetch(
          `${API_URL}/api/email/preferences/by-token?token=${encodeURIComponent(token!)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ preferences }),
          }
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || "Failed to save preferences");
        }

        setPreferences(data.preferences);
      } else {
        const response = await apiPut<{ preferences: EmailPreferences }>(
          "/api/email/preferences",
          { preferences }
        );

        if (response.error) {
          throw new Error(response.error.message);
        }

        setPreferences(response.data?.preferences || preferences);
      }

      setHasChanges(false);
      setSaveSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save preferences");
    } finally {
      setIsSaving(false);
    }
  }, [preferences, isTokenMode, token]);

  // Pause emails
  const pauseEmails = useCallback(
    async (days: number) => {
      setIsSaving(true);
      setError(null);

      try {
        const response = await apiPost<{ preferences: EmailPreferences }>(
          "/api/email/preferences/pause",
          { days }
        );

        if (response.error) {
          throw new Error(response.error.message);
        }

        setPreferences(response.data?.preferences || null);
        setShowPauseModal(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to pause emails");
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  // Resume emails
  const resumeEmails = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    try {
      const response = await apiPost<{ preferences: EmailPreferences }>(
        "/api/email/preferences/resume",
        {}
      );

      if (response.error) {
        throw new Error(response.error.message);
      }

      setPreferences(response.data?.preferences || null);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resume emails");
    } finally {
      setIsSaving(false);
    }
  }, []);

  // Unsubscribe from all
  const unsubscribeAll = useCallback(async () => {
    if (!preferences) return;

    const updates: Partial<EmailPreferences> = {
      marketing: false,
      product_updates: false,
      tips_and_tricks: false,
      sequences: false,
    };

    setPreferences((prev) => (prev ? { ...prev, ...updates } : prev));
    setShowUnsubscribeModal(false);
    setHasChanges(true);
  }, [preferences]);

  // Check if emails are paused
  const isPaused =
    preferences?.paused_until && new Date(preferences.paused_until) > new Date();

  const pausedUntilDate = isPaused
    ? new Date(preferences!.paused_until!).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : null;

  // Render loading state
  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Render no access state
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100 px-4 py-12">
        <div className="max-w-md mx-auto">
          <NoAccessCard />
        </div>
      </div>
    );
  }

  // Render error state (invalid/expired token)
  if (error && !preferences) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100 px-4 py-12">
        <div className="max-w-md mx-auto">
          <ErrorCard message={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100">
      {/* Decorative header bar */}
      <div
        className="h-2 bg-gradient-to-r from-meadow-400 via-sky-400 to-magic-400"
        style={{
          backgroundSize: "200% 100%",
          animation: "gradientShift 8s ease infinite",
        }}
      />

      <div className="px-4 py-8 pb-32">
        <div className="max-w-lg mx-auto space-y-6">
          {/* Header */}
          <header className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white shadow-lg mb-4">
              <MailIcon className="w-8 h-8 text-magic-500" />
            </div>
            <h1 className="font-display font-bold text-display-sm text-neutral-800">
              Email Preferences
            </h1>
            {maskedEmail && (
              <p className="font-body text-body-md text-neutral-500 mt-2">
                Managing preferences for {maskedEmail}
              </p>
            )}
            {user?.email && !isTokenMode && (
              <p className="font-body text-body-md text-neutral-500 mt-2">
                Managing preferences for {user.email}
              </p>
            )}
          </header>

          {/* Paused Banner */}
          {isPaused && (
            <div
              className="
                flex items-center gap-4
                px-5 py-4
                bg-sunshine-50
                border-2 border-sunshine-200
                rounded-2xl
              "
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-sunshine-100">
                <PauseIcon className="w-5 h-5 text-sunshine-700" />
              </div>
              <div className="flex-1">
                <p className="font-body font-semibold text-body-lg text-sunshine-800">
                  Emails Paused
                </p>
                <p className="font-body text-body-md text-sunshine-700">
                  Until {pausedUntilDate}
                </p>
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={resumeEmails}
                isLoading={isSaving}
                className="!min-h-[44px]"
              >
                Resume
              </Button>
            </div>
          )}

          {/* Save status bar */}
          {(hasChanges || saveSuccess || error) && (
            <div
              className={`
                sticky top-4 z-10
                flex items-center justify-between
                px-4 py-3
                rounded-xl
                border-2
                shadow-lg
                backdrop-blur-sm
                animate-[slideDown_200ms_ease-out]
                ${
                  error
                    ? "bg-fairy-50/95 border-fairy-300"
                    : saveSuccess
                      ? "bg-meadow-50/95 border-meadow-300"
                      : "bg-sunshine-100/95 border-sunshine-300"
                }
              `}
            >
              {error ? (
                <>
                  <span className="font-body font-medium text-fairy-800">
                    {error}
                  </span>
                  <button
                    onClick={() => setError(null)}
                    className="text-fairy-600 hover:text-fairy-800"
                  >
                    Dismiss
                  </button>
                </>
              ) : saveSuccess ? (
                <>
                  <span className="font-body font-medium text-meadow-800 flex items-center gap-2">
                    <CheckIcon className="w-5 h-5" />
                    Preferences saved
                  </span>
                </>
              ) : (
                <>
                  <span className="font-body font-medium text-sunshine-800">
                    You have unsaved changes
                  </span>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={savePreferences}
                    isLoading={isSaving}
                    className="!min-h-[44px] !px-5"
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Email Types Section */}
          <SettingsSection
            title="Email Types"
            description="Choose which emails you'd like to receive"
            icon={<InboxIcon />}
            accent="magic"
          >
            <Toggle
              checked={preferences?.product_updates ?? true}
              onChange={(v) => updatePreference("product_updates", v)}
              label="Product Updates"
              description="New challenges, app updates, and improvements"
            />

            <div className="h-px bg-neutral-100" />

            <Toggle
              checked={preferences?.tips_and_tricks ?? true}
              onChange={(v) => updatePreference("tips_and_tricks", v)}
              label="Tips & Educational Content"
              description="Learning tips, activity ideas, parenting advice"
            />

            <div className="h-px bg-neutral-100" />

            <Toggle
              checked={preferences?.sequences ?? true}
              onChange={(v) => updatePreference("sequences", v)}
              label="Welcome & Onboarding Emails"
              description="Getting started guides and helpful walkthroughs"
            />

            <div className="h-px bg-neutral-100" />

            <Toggle
              checked={preferences?.marketing ?? false}
              onChange={(v) => updatePreference("marketing", v)}
              label="Promotional Emails"
              description="Special offers, discounts, and announcements"
            />
          </SettingsSection>

          {/* Frequency Section */}
          <SettingsSection
            title="Email Frequency"
            description="Control how often you hear from us"
            icon={<ClockIcon />}
            accent="sky"
          >
            <div className="space-y-3">
              <FrequencyOption
                value="normal"
                label="Normal"
                description="Receive all emails as scheduled"
                selected={preferences?.frequency === "normal"}
                onChange={() => updatePreference("frequency", "normal")}
              />
              <FrequencyOption
                value="reduced"
                label="Reduced"
                description="Maximum 1 email per week"
                selected={preferences?.frequency === "reduced"}
                onChange={() => updatePreference("frequency", "reduced")}
              />
              <FrequencyOption
                value="minimal"
                label="Minimal"
                description="Only essential account notifications"
                selected={preferences?.frequency === "minimal"}
                onChange={() => updatePreference("frequency", "minimal")}
              />
            </div>
          </SettingsSection>

          {/* Actions Section */}
          <SettingsSection
            title="Quick Actions"
            description="Take a break or unsubscribe"
            icon={<SettingsIcon />}
            accent="meadow"
          >
            {/* Pause button - only for authenticated users */}
            {!isTokenMode && !isPaused && (
              <button
                type="button"
                onClick={() => setShowPauseModal(true)}
                className="
                  w-full
                  flex items-center gap-4
                  py-3 px-1
                  text-left
                  font-body text-body-md text-neutral-700
                  hover:text-magic-600
                  transition-colors
                  group
                "
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-neutral-100 group-hover:bg-magic-100 transition-colors">
                  <PauseIcon className="w-5 h-5 text-neutral-500 group-hover:text-magic-600" />
                </span>
                <div className="flex-1">
                  <span className="font-semibold">Pause all emails</span>
                  <p className="text-body-sm text-neutral-500">
                    Take a break for 30 days
                  </p>
                </div>
                <ChevronIcon />
              </button>
            )}

            {!isTokenMode && !isPaused && <div className="h-px bg-neutral-100" />}

            {/* Unsubscribe button */}
            <button
              type="button"
              onClick={() => setShowUnsubscribeModal(true)}
              className="
                w-full
                flex items-center gap-4
                py-3 px-1
                text-left
                font-body text-body-md text-fairy-600
                hover:text-fairy-700
                transition-colors
                group
              "
            >
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-fairy-50 group-hover:bg-fairy-100 transition-colors">
                <UnsubscribeIcon className="w-5 h-5 text-fairy-500 group-hover:text-fairy-600" />
              </span>
              <div className="flex-1">
                <span className="font-semibold">Unsubscribe from all</span>
                <p className="text-body-sm text-fairy-400">
                  Stop all marketing emails
                </p>
              </div>
              <ChevronIcon className="text-fairy-300" />
            </button>
          </SettingsSection>

          {/* Footer note */}
          <div className="text-center pt-4">
            <p className="font-body text-body-sm text-neutral-400">
              You'll still receive essential account notifications like password
              resets and billing confirmations.
            </p>
            {isTokenMode && (
              <p className="font-body text-body-sm text-neutral-400 mt-2">
                <Link to="/login" className="text-magic-500 hover:text-magic-600">
                  Log in
                </Link>{" "}
                for more account options.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Pause Modal */}
      <PauseModal
        isOpen={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onPause={pauseEmails}
        isLoading={isSaving}
      />

      {/* Unsubscribe Modal */}
      <UnsubscribeModal
        isOpen={showUnsubscribeModal}
        onClose={() => setShowUnsubscribeModal(false)}
        onConfirm={unsubscribeAll}
      />

      {/* Add animation keyframes */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
    </div>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

interface FrequencyOptionProps {
  value: string;
  label: string;
  description: string;
  selected: boolean;
  onChange: () => void;
}

function FrequencyOption({
  value,
  label,
  description,
  selected,
  onChange,
}: FrequencyOptionProps) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`
        w-full
        flex items-center gap-4
        p-4
        rounded-xl
        border-2
        transition-all duration-200
        ${
          selected
            ? "bg-sky-50 border-sky-400 shadow-[0_2px_8px_rgba(74,144,217,0.15)]"
            : "bg-white border-neutral-200 hover:border-neutral-300"
        }
      `}
    >
      {/* Radio circle */}
      <div
        className={`
          w-6 h-6
          rounded-full
          border-2
          flex items-center justify-center
          transition-all duration-200
          ${selected ? "border-sky-500 bg-sky-500" : "border-neutral-300"}
        `}
      >
        {selected && (
          <div className="w-2.5 h-2.5 rounded-full bg-white" />
        )}
      </div>

      {/* Text */}
      <div className="flex-1 text-left">
        <span
          className={`
            font-body font-semibold text-body-lg
            ${selected ? "text-sky-700" : "text-neutral-700"}
          `}
        >
          {label}
        </span>
        <p className="font-body text-body-sm text-neutral-500">{description}</p>
      </div>
    </button>
  );
}

interface PauseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPause: (days: number) => void;
  isLoading: boolean;
}

function PauseModal({ isOpen, onClose, onPause, isLoading }: PauseModalProps) {
  const [days, setDays] = useState(30);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Pause Emails" size="sm">
      <div className="space-y-5">
        <p className="font-body text-body-md text-neutral-600">
          Take a break from JumpBean emails. We'll automatically resume sending
          after your pause period ends.
        </p>

        <div className="space-y-3">
          <label className="font-body font-semibold text-body-md text-neutral-700">
            Pause for:
          </label>
          <div className="flex gap-3">
            {[7, 30, 60, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDays(d)}
                className={`
                  flex-1
                  py-3 px-2
                  rounded-xl
                  font-body font-semibold text-body-md
                  border-2
                  transition-all duration-200
                  ${
                    days === d
                      ? "bg-sky-50 border-sky-400 text-sky-700"
                      : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"
                  }
                `}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            size="md"
            onClick={onClose}
            className="!min-h-[56px]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            fullWidth
            size="md"
            onClick={() => onPause(days)}
            isLoading={isLoading}
            className="!min-h-[56px]"
          >
            Pause Emails
          </Button>
        </div>
      </div>
    </Modal>
  );
}

interface UnsubscribeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

function UnsubscribeModal({ isOpen, onClose, onConfirm }: UnsubscribeModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Unsubscribe" size="sm">
      <div className="space-y-5">
        <div className="p-4 bg-fairy-50 rounded-xl border border-fairy-200">
          <p className="font-body text-body-md text-fairy-700">
            This will disable all marketing emails including product updates,
            tips, and promotional content.
          </p>
        </div>

        <p className="font-body text-body-md text-neutral-600">
          You'll still receive essential account notifications like password
          resets and billing confirmations.
        </p>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            fullWidth
            size="md"
            onClick={onClose}
            className="!min-h-[56px]"
          >
            Cancel
          </Button>
          <button
            type="button"
            onClick={onConfirm}
            className="
              flex-1
              min-h-[56px]
              px-6
              font-display font-bold text-body-lg
              rounded-full
              bg-fairy-500 text-white
              border-3 border-fairy-600
              shadow-[0_4px_0_0_rgba(192,24,91,1),0_6px_12px_rgba(0,0,0,0.15)]
              hover:bg-fairy-600
              transition-all duration-200
            "
          >
            Unsubscribe
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="
          w-12 h-12
          border-4 border-neutral-200
          border-t-magic-500
          rounded-full
          animate-spin
        "
      />
      <p className="font-body text-body-md text-neutral-500">
        Loading preferences...
      </p>
    </div>
  );
}

function NoAccessCard() {
  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-neutral-200 p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-neutral-100 flex items-center justify-center">
        <LockIcon className="w-8 h-8 text-neutral-400" />
      </div>
      <h2 className="font-display font-bold text-body-xl text-neutral-800 mb-2">
        Access Required
      </h2>
      <p className="font-body text-body-md text-neutral-500 mb-6">
        Please log in to manage your email preferences, or use the link from one
        of our emails.
      </p>
      <Link
        to="/login"
        className="
          inline-flex items-center justify-center
          min-h-[56px] px-8
          font-display font-bold text-body-lg
          rounded-full
          bg-gradient-to-r from-magic-400 to-magic-500
          text-white
          border-3 border-magic-600
          shadow-[0_4px_0_0_rgba(126,68,173,1),0_6px_12px_rgba(0,0,0,0.15)]
          hover:shadow-[0_6px_0_0_rgba(126,68,173,1),0_8px_16px_rgba(0,0,0,0.2)]
          transition-all duration-200
        "
      >
        Log In
      </Link>
    </div>
  );
}

interface ErrorCardProps {
  message: string;
}

function ErrorCard({ message }: ErrorCardProps) {
  return (
    <div className="bg-white rounded-2xl shadow-lg border-2 border-fairy-200 p-8 text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-fairy-50 flex items-center justify-center">
        <AlertIcon className="w-8 h-8 text-fairy-500" />
      </div>
      <h2 className="font-display font-bold text-body-xl text-neutral-800 mb-2">
        Unable to Load Preferences
      </h2>
      <p className="font-body text-body-md text-neutral-500 mb-6">{message}</p>
      <p className="font-body text-body-sm text-neutral-400">
        The link may have expired.{" "}
        <Link to="/login" className="text-magic-500 hover:text-magic-600">
          Log in
        </Link>{" "}
        to manage your preferences.
      </p>
    </div>
  );
}

// ============================================================================
// Icons
// ============================================================================

function MailIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 3H4.99c-1.11 0-1.98.89-1.98 2L3 19c0 1.1.88 2 1.99 2H19c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 12h-4c0 1.66-1.35 3-3 3s-3-1.34-3-3H4.99V5H19v10z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
  );
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
  );
}

function UnsubscribeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z" />
      <path d="M14 14.5l1.5-1.5 1.5 1.5 1.5-1.5-1.5-1.5 1.5-1.5-1.5-1.5-1.5 1.5-1.5-1.5-1.5 1.5 1.5 1.5-1.5 1.5z" />
    </svg>
  );
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      className={`w-5 h-5 text-neutral-400 ${className || ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
    </svg>
  );
}

// Add apiPut to API helpers if it doesn't exist
async function apiPut<T>(
  endpoint: string,
  body: unknown
): Promise<{ data?: T; error?: { message: string; status: number } }> {
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

  try {
    const { supabase } = await import("../lib/supabase");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (supabase) {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.access_token) {
        headers["Authorization"] = `Bearer ${session.access_token}`;
      }
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(body),
    });

    const json = await response.json();

    if (!response.ok) {
      return {
        error: json.error || {
          message: "Request failed",
          status: response.status,
        },
      };
    }

    return { data: json.data ?? json };
  } catch (error) {
    console.error("API PUT error:", error);
    return {
      error: {
        message: error instanceof Error ? error.message : "Network error",
        status: 0,
      },
    };
  }
}

export default EmailPreferencesPage;
